import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiArrowRight, FiAward, FiUsers, FiCalendar } from 'react-icons/fi';
import { LuLeaf } from 'react-icons/lu';
import colors from '../../styles/colors';

const sway = keyframes`
  0%, 100% { transform: translateX(0) rotate(-5deg); }
  50% { transform: translateX(15px) rotate(5deg); }
`;

const drift = keyframes`
  0% { transform: translateY(-100px) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: url('/images/hero-bg.jpg') center/cover no-repeat;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${colors.gradient.hero};
    z-index: 1;
  }
`;

const FloatingLeaf = styled.div`
  position: absolute;
  color: ${colors.accent.gold};
  opacity: 0.25;
  font-size: ${props => props.$size || '2rem'};
  z-index: 0;
  animation: ${drift} ${props => props.$duration || '15s'} linear infinite;
  animation-delay: ${props => props.$delay || '0s'};
  left: ${props => props.$left || '10%'};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 120px 40px 80px;
  width: 100%;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 120px 20px 60px;
  }
`;

const Content = styled.div`
  max-width: 750px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: rgba(201, 162, 39, 0.15);
  border: 1px solid rgba(201, 162, 39, 0.3);
  border-radius: 50px;
  color: ${colors.accent.gold};
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 28px;
  backdrop-filter: blur(10px);

  svg {
    animation: ${sway} 3s ease-in-out infinite;
  }
`;

const Title = styled.h1`
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  color: ${colors.text.white};
  margin-bottom: 24px;
  line-height: 1.1;
  font-weight: 800;

  span {
    color: ${colors.accent.gold};
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 40px;
  max-width: 580px;
  font-weight: 500;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 60px;
`;

const PrimaryBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 36px;
  background: ${colors.gradient.gold};
  color: ${colors.primary.dark};
  font-weight: 700;
  font-size: 1rem;
  border-radius: 50px;
  box-shadow: ${colors.shadowGold};

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(201, 162, 39, 0.5);
  }

  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(5px);
  }
`;

const SecondaryBtn = styled.button`
  padding: 18px 36px;
  background: transparent;
  color: ${colors.text.white};
  font-weight: 600;
  font-size: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;

  &:hover {
    border-color: ${colors.accent.gold};
    color: ${colors.accent.gold};
    background: rgba(201, 162, 39, 0.1);
  }
`;

const Stats = styled.div`
  display: flex;
  gap: 50px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 30px;
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(201, 162, 39, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.accent.gold};
  font-size: 1.3rem;
  margin-bottom: 12px;
`;

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${colors.text.white};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${colors.text.muted};
  font-weight: 500;
  margin-top: 4px;
`;

export default function Hero() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeroSection id="hero">
      {/* Floating leaves animation */}
      <FloatingLeaf $size="3rem" $left="5%" $duration="18s" $delay="0s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="2rem" $left="12%" $duration="22s" $delay="3s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="2.5rem" $left="22%" $duration="16s" $delay="7s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="1.5rem" $left="35%" $duration="19s" $delay="4s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="2.2rem" $left="45%" $duration="21s" $delay="9s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="1.8rem" $left="55%" $duration="17s" $delay="2s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="2.8rem" $left="65%" $duration="20s" $delay="6s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="2rem" $left="75%" $duration="18s" $delay="1s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="2.4rem" $left="85%" $duration="23s" $delay="5s"><LuLeaf /></FloatingLeaf>
      <FloatingLeaf $size="1.6rem" $left="92%" $duration="16s" $delay="8s"><LuLeaf /></FloatingLeaf>

      <Container>
        <Content>
          <Badge>
            <LuLeaf /> Serving the Community Since 2005
          </Badge>
          <Title>
            Transform Your<br />
            Outdoor <span>Space</span>
          </Title>
          <Subtitle>
            From landscape design to lawn maintenance, garden supplies to snow removal —
            Greenridge has been your trusted partner for nearly two decades.
          </Subtitle>
          <Buttons>
            <PrimaryBtn onClick={() => scrollTo('contact')}>
              Get Free Estimate <FiArrowRight />
            </PrimaryBtn>
            <SecondaryBtn onClick={() => scrollTo('services')}>
              View Services
            </SecondaryBtn>
          </Buttons>
          <Stats>
            <Stat>
              <StatIcon><FiCalendar /></StatIcon>
              <StatNumber>19+</StatNumber>
              <StatLabel>Years Experience</StatLabel>
            </Stat>
            <Stat>
              <StatIcon><FiUsers /></StatIcon>
              <StatNumber>1000+</StatNumber>
              <StatLabel>Happy Clients</StatLabel>
            </Stat>
            <Stat>
              <StatIcon><FiAward /></StatIcon>
              <StatNumber>100%</StatNumber>
              <StatLabel>Satisfaction</StatLabel>
            </Stat>
          </Stats>
        </Content>
      </Container>
    </HeroSection>
  );
}
